// src/routes/statistics/report/hooks/useAutoCsvDownload.js

import { useEffect } from 'react';
import { format } from 'date-fns';
import { downloadCsvFile } from '../utils/csvDownload';

const useAutoCsvDownload = ({
  enabled,
  autoDownloadTime,
  fetchCsv,
  setIsDownloading,
}) => {
  useEffect(() => {
    if (!enabled) return;

    let timeoutId = null;
    let isAutoDownloading = false;

    const getNextDownloadTime = () => {
      const now = new Date();
      const [setHour, setMinute] = autoDownloadTime.split(':').map(Number);

      const targetTime = new Date(now);
      targetTime.setHours(setHour, setMinute, 0, 0);

      if (targetTime <= now) {
        targetTime.setDate(targetTime.getDate() + 1);
      }

      return targetTime;
    };

    const executeDownload = async () => {
      if (isAutoDownloading) return;

      const now = new Date();
      const todayStr = format(now, 'yyyy-MM-dd');
      const currentTime = format(now, 'HH:mm');

      const lastDownloadDate = localStorage.getItem('lastAutoDownloadDate');
      const lastDownloadTime = localStorage.getItem('lastAutoDownloadTime');

      let shouldDownload = false;

      if (lastDownloadDate !== todayStr) {
        shouldDownload = true;
      } else if (lastDownloadTime) {
        const [lastHour, lastMinute] = lastDownloadTime.split(':').map(Number);
        const [setHour, setMinute] = autoDownloadTime.split(':').map(Number);

        const lastTimeMinutes = lastHour * 60 + lastMinute;
        const setTimeMinutes = setHour * 60 + setMinute;

        shouldDownload = lastTimeMinutes < setTimeMinutes;
      } else {
        shouldDownload = true;
      }

      if (!shouldDownload) {
        scheduleNext();
        return;
      }

      isAutoDownloading = true;
      localStorage.setItem('lastAutoDownloadDate', todayStr);
      localStorage.setItem('lastAutoDownloadTime', currentTime);

      setIsDownloading(true);
      try {
        const csv = await fetchCsv(now);
        downloadCsvFile(csv, `태양광발전일지_개별_${todayStr}.csv`);
      } catch (error) {
        console.error('[자동 CSV 다운로드] 다운로드 중 예외 발생:', error);
      } finally {
        setIsDownloading(false);
        isAutoDownloading = false;
        scheduleNext();
      }
    };

    const scheduleNext = () => {
      if (timeoutId) clearTimeout(timeoutId);
      const nextTime = getNextDownloadTime();
      const delay = nextTime.getTime() - Date.now();
      timeoutId = setTimeout(executeDownload, delay);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = new Date();
        const [setHour, setMinute] = autoDownloadTime.split(':').map(Number);

        if (now.getHours() === setHour && now.getMinutes() === setMinute) {
          const todayStr = format(now, 'yyyy-MM-dd');
          const lastDownloadDate = localStorage.getItem('lastAutoDownloadDate');

          if (lastDownloadDate !== todayStr) {
            executeDownload();
            return;
          }
        }

        scheduleNext();
      }
    };

    scheduleNext();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, autoDownloadTime, fetchCsv, setIsDownloading]);
};

export default useAutoCsvDownload;